<template>
	<div>
		<div :class="`c-search ${id} ${(largeInput) ? 'c-search--lg' : ''} ${(!useAnimation || isOpen) ? 'is-open' : ''}`">
		<form :action="url" method="get" role="search" class="c-search__form">
			<label :for="id" class="t-visually-hidden">Sök</label>
			<div :class="`c-search__controls ${(isOpen && useAnimation) ? 'c-search__controls--open' : ''} ${(isAutocompleteVisible && items.length > 0) ? 'c-search--open': ''}`" ref="controls">
				<input
					type="search"
					:id="id"
					name="q"
					class="c-search__input"
					v-model="searchValue"
					placeholder="Sök på mkbfastighet.se"
					ref="searchInput"
					tabindex="2"
					@input="e => onInput(e.target.value)"
					@keydown="listKeyPress($event)"
					autocomplete="off"
					:aria-expanded="isAutocompleteVisible && items.length > 0 ? true : false"
					:aria-owns="isAutocompleteVisible ? id+'_results' : null"
					aria-autocomplete="both"/>
					<template v-if="isLoading">
						<div class="c-search__loading-wrapper" aria-live="assertive">
							<div class="c-search__loading">
								<div class="c-search__circle" aria-hidden="true"></div>
								<span class="c-search__text t-visually-hidden">Söker fram alternativ...</span>
							</div>
						</div>
					</template>
					<template v-else>
						<button v-if="isOpen && useAnimation" type="submit" class="c-search__submit" ref="submit">
							<span class="t-visually-hidden c-search__button-text">{{submitText}}</span>
							<v-icon name="icon-search" size="s"></v-icon>
						</button>
						<button v-else-if="!isOpen && !useAnimation" type="submit" class="c-search__submit" ref="submit">
							<span class="t-visually-hidden c-search__button-text">{{submitText}}</span>
							<v-icon name="icon-search" size="s"></v-icon>
						</button>
						<button v-else type="submit" @click="openSearch" class="c-search__submit" ref="submit">
							<span class="t-visually-hidden c-search__button-text">{{submitText}}</span>
							<v-icon name="icon-search" size="s" css-modifiers="c-search--search-icon"></v-icon>
						</button>
					</template>
			</div>
			<transition name="fade">
				<ul v-if="isAutocompleteVisible && items.length > 0"
					:class="`c-search__autocomplete ${(isAutocompleteVisible && items.length > 0) ? 'c-search__autocomplete--open': ''}`"
					:id="id +'_results'"
					role="listbox"
					ref="list">
						<li v-for="(item, i) in items"
							:key="i"
							@click="setResult(item, i)"
							class="c-search__autocomplete-result t-text-color--emphasis"
							:class="{ 'is-active': i === selectedIndex }"
							role="option"
							:aria-selected="i === selectedIndex ? true : false"
							:inner-html.prop="item | highlight(searchValue)">
						</li>
				</ul>
			</transition>
		</form>
		<button v-if="useAnimation" class="c-button c-button--noboxshadow c-search__close" @click="closeSearch" type="button" ref="close">
			<span class="t-visually-hidden">Stäng sök</span>
			<v-icon name="icon-close" size="xxs" css-modifiers="c-search--close-icon"></v-icon>
		</button>
		</div>
	</div>
</template>

<script>
import VIcon from '../../components/icon/VIcon.vue';
import clickOutside from '../../js/utilities/vue-mixins/click-outside.js';
import toggleTabIndex from '../../js/utilities/toggle-tab-index/u-toggle-tab-index.js';
import toggleAriaHidden from '../../js/utilities/toggle-aria-hidden/u-toggle-aria-hidden.js';

export default {
    name: 'VSearch',
	components: {
		VIcon
	},
	mixins: [clickOutside],
	props: {
		url: {
			type: String,
			default: '/search-results.html'
		},
		useAnimation: {
			type: Boolean,
			default: false
		},
		largeInput: {
			type: Boolean,
			default: false
		},
		apiUrl: {
			type: String,
			default: ''
		},
		id: {
			type: String,
			default: 'default'
		},
		items: {
			type: Array,
			default: () => []
		},
		maxIndex: Number,
		inputValue: {
			type: String,
			default: ''
		},
		isLoading: {
			type: Boolean,
			default: false
		}
	},
	filters: {
		highlight(word, query) {
			var regEx = new RegExp(query, 'i');
			return word.toString().replace(regEx, function(matched) {
				return ('<span class=\'c-search__highlight\'>' + matched + '</span>');
			});
		}
	},
	data() {
		return {
			isOpen: false,
			submitText: 'Öppna Sök',
			isAutocompleteVisible: true,
			selectedIndex: 0,
			scrollPos: 0,
			searchValue: this.inputValue
		}
	},
	methods: {
		onInput(value) {
			this.$emit('getItems', value);
			this.showAutoComplete();
		},
		/**
		 * Open the search input field
		 */
		openSearch(e) {
			e.preventDefault();
			this.$refs.submit.setAttribute('aria-pressed', 'true');
			toggleTabIndex(this.$refs.searchInput);
			if (this.$refs.close) {
				toggleTabIndex(this.$refs.close);
			}
			toggleAriaHidden(this.$refs.searchInput);
			this.isOpen = !this.isOpen;
			this.submitText = 'Sök';
			this.$refs.searchInput.focus();
		},
		/**
		 * Close the search input field
		 */
		closeSearch() {
			if(this.useAnimation) {
				toggleTabIndex(this.$refs.searchInput);
				if (this.$refs.close) {
					toggleTabIndex(this.$refs.close);
				}
				toggleAriaHidden(this.$refs.searchInput);
				this.isOpen = false;
				this.submitText = 'Öppna Sök';
				this.isAutocompleteVisible = false;
				if(this.$refs.submit) {
					this.$refs.submit.setAttribute('aria-pressed', 'false');
				}
			}
		},
		showAutoComplete() {
			this.isAutocompleteVisible = true;
		},
		setResult(item, i) {
			this.selectedIndex = i;
			this.searchValue = item;
			this.isAutocompleteVisible = false;
		},
		/**
		 * Handle list keypress
		 */
		listKeyPress(event) {
			switch (event.keyCode) {
				case 40: /* Arrow down */
					event.preventDefault();
					if (this.selectedIndex === this.maxIndex) {
						/* If selected option is at bottom, start from top */
						this.selectedIndex = 0;
					} else {
						this.selectedIndex = this.selectedIndex + 1;
						this.scroll('down');
					}
					break;
				case 38: /* Arrow up */
					event.preventDefault();
					if (this.selectedIndex === 0) {
						/* If selected option is at top, start from bottom */
						this.selectedIndex = this.maxIndex;
					} else {
						this.selectedIndex = this.selectedIndex - 1;
						this.scroll('up');
					}
					break;
				case 13: /* Enter */
					let getSelectedName = this.items.find((item, index) => index === this.selectedIndex);
					if (this.selectedIndex !== 0) {
						/* If autocomplete generated any hits */
						this.setResult(getSelectedName, this.selectedIndex);
					}
					break;
				case 27: /* Escape */
					this.isAutocompleteVisible = false;
					break;
				default:
					break;
			}
		},
		scroll(direction) {
			if (direction === 'up') {
				this.$refs.list.scrollTop -= 33;
			} else {
				this.$refs.list.scrollTop += 33;
			}
			this.scrollPos = this.$refs.list.offsetTop;
		}
	},
	watch: {
		selectedIndex() {
			this.$refs.list.setAttribute('aria-activedescendant', this.id + '-option-' + this.selectedIndex);
		},
		inputValue: function(val) {
			this.searchValue = val;
		}
	},
	created() {
		this.$on('click-outside', function () {
			if(this.isOpen) {
				this.closeSearch();
			}
			this.isAutocompleteVisible = false;
		});
	},
	mounted() {
		// Remove hidden input from tab
		toggleTabIndex(this.$refs.searchInput);
		if (this.$refs.close) {
			toggleTabIndex(this.$refs.close);
		}

		// Add aria attribute to button
		if(this.$refs.submit) {
			this.$refs.submit.setAttribute('aria-pressed', 'false');
		}

		this.isAutocompleteVisible = false;
	}
}
</script>
